

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, X, ArrowRight, Trash2, CheckCircle2, ChevronLeft, Settings, Filter, Check, Box, Loader2, Info } from 'lucide-react';
import { ResourceRef, IRepositories, ResourceEntity, SystemEntity, ResourcePlanEntity, ResourceKind } from '../../types';
import VirtualList from '../atoms/VirtualList';

interface ResourceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (resources: ResourceRef[]) => void;
  repositories: IRepositories;
}

interface DraftConfig {
    planId: string;
}

const ResourceSelectionModal: React.FC<ResourceSelectionModalProps> = ({ isOpen, onClose, onConfirm, repositories }) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  
  // Step 2 state: Configuration for each selected resource
  const [configs, setConfigs] = useState<Record<string, DraftConfig>>({});
  
  // Bulk actions state
  const [bulkPlan, setBulkPlan] = useState('');

  // Async Data State
  const [resources, setResources] = useState<ResourceEntity[]>([]);
  const [plans, setPlans] = useState<ResourcePlanEntity[]>([]);
  const [systemMap, setSystemMap] = useState<Map<string, SystemEntity>>(new Map());
  const [loading, setLoading] = useState(false);

  // Search Debounce
  const searchTimeoutRef = useRef<any>(null);

  // Reset & Init when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedIds(new Set());
      setConfigs({});
      setSearchTerm('');
      setBulkPlan('');
      
      // Initial Fetch
      fetchPlans();
      fetchResources('');
    }
  }, [isOpen]);

  const fetchPlans = async () => {
      try {
          const result = await repositories.getPlans({ pageSize: 100 });
          setPlans(result.items);
      } catch (err) {
          console.error("Failed to fetch plans", err);
      }
  };

  const fetchResources = async (term: string) => {
      setLoading(true);
      try {
          const result = await repositories.getResources({ 
              filter: term, 
              pageSize: 1000 // Increase page size now that we have virtualization
          });
          setResources(result.items);

          // Resolve Systems for these resources
          const sysIds = new Set<string>();
          result.items.forEach(r => {
              if (r.systemId) sysIds.add(r.systemId);
          });
          
          if (sysIds.size > 0) {
              const sysNames = await repositories.resolveSystems(Array.from(sysIds));
              setSystemMap(prev => new Map([...prev, ...sysNames]));
          }
      } catch (err) {
          console.error("Failed to fetch resources", err);
      } finally {
          setLoading(false);
      }
  };

  // Handle Search Input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setSearchTerm(val);
      
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
          fetchResources(val);
      }, 300);
  };

  // --- Handlers ---

  const toggleSelection = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
      const newConfigs = { ...configs };
      delete newConfigs[id];
      setConfigs(newConfigs);
    } else {
      next.add(id);
      setConfigs(prev => ({
        ...prev,
        [id]: { planId: '' } // Initialize empty config
      }));
    }
    setSelectedIds(next);
  };

  const updateConfig = (id: string, field: keyof DraftConfig, value: string) => {
    setConfigs(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const applyBulkPlan = (planId: string) => {
    setBulkPlan(planId);
    if (!planId) return;

    // Smart Bulk Apply: Only apply if system matches and not fixed
    const selectedPlan = plans.find(p => p.id === planId);
    if (!selectedPlan) return;

    setConfigs(prev => {
        const next = { ...prev };
        selectedIds.forEach(id => {
            const resource = resources.find(r => r.id === id);
            // Valid if: Resource exists AND systems match AND resource is not Fixed
            if (resource && 
                resource.systemId === selectedPlan.systemId && 
                resource.resourceKind !== ResourceKind.Fixed
            ) {
                 if (next[id]) next[id] = { ...next[id], planId: planId };
            }
        });
        return next;
    });
  };

  const handleRemoveInStep2 = (id: string) => {
    const next = new Set(selectedIds);
    next.delete(id);
    setSelectedIds(next);
    const newConfigs = { ...configs };
    delete newConfigs[id];
    setConfigs(newConfigs);
    
    // If all removed, go back
    if (next.size === 0) setStep(1);
  };

  const handleConfirm = () => {
    const results: ResourceRef[] = [];
    selectedIds.forEach(id => {
        const conf = configs[id];
        const resource = resources.find(r => r.id === id); 
        
        if (resource && resource.systemId) {
            // Logic for Fixed resources
            if (resource.resourceKind === ResourceKind.Fixed) {
                results.push({
                    systemId: resource.systemId,
                    resourceId: id,
                    resourcePlanId: null // Fixed resources do not need a plan
                });
            } else if (conf && conf.planId) {
                // Logic for Single/Collection
                results.push({
                    systemId: resource.systemId,
                    resourceId: id,
                    resourcePlanId: conf.planId
                });
            }
        }
    });
    onConfirm(results);
    onClose();
  };

  const isValid = () => {
    // Check if every selected resource has a plan (unless Fixed)
    return Array.from(selectedIds).every(id => {
        const resource = resources.find(r => r.id === id);
        // Fixed resources are valid without a plan
        if (resource?.resourceKind === ResourceKind.Fixed) return true;

        const c = configs[id];
        return c && c.planId;
    });
  };

  const resolveSystemName = (sysId?: string) => {
      if (!sysId) return 'Unknown';
      const sys = systemMap.get(sysId);
      return sys ? sys.name : sysId;
  };

  // Group plans by system for bulk dropdown
  const plansBySystem = useMemo(() => {
    const map = new Map<string, ResourcePlanEntity[]>();
    plans.forEach(p => {
        const sys = p.systemId;
        if (!map.has(sys)) map.set(sys, []);
        map.get(sys)?.push(p);
    });
    return map;
  }, [plans]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden border border-slate-200">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Add Resources</h2>
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
              <span className={step === 1 ? "text-teal-600 font-semibold" : ""}>1. Select Resources</span>
              <span className="text-slate-300">/</span>
              <span className={step === 2 ? "text-teal-600 font-semibold" : ""}>2. Assign Plans</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-400 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative flex flex-col">
            
            {/* STEP 1: SELECT */}
            {step === 1 && (
                <>
                    {/* Search Bar */}
                    <div className="p-4 border-b border-slate-100 flex gap-4 bg-white flex-shrink-0">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <input 
                                type="text" 
                                placeholder="Search resources by name, ID or description..." 
                                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                autoFocus
                            />
                        </div>
                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200 text-slate-600 text-sm min-w-[100px] justify-center">
                            {loading ? (
                                <Loader2 size={16} className="animate-spin text-teal-600" />
                            ) : (
                                <>
                                    <Filter size={16} />
                                    <span className="font-medium">{resources.length} Found</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Table View Header (Fixed) */}
                    <div className="bg-white border-b border-slate-200 shadow-sm z-10 flex-shrink-0">
                       <div className="flex text-left text-sm">
                           <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[60px] text-center">
                              <div className="w-4 h-4 mx-auto border-2 border-slate-300 rounded"></div>
                           </div>
                           <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider flex-1">Resource</div>
                           <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[25%] hidden md:block">Description</div>
                           <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[15%]">Type</div>
                           <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[20%]">System</div>
                       </div>
                    </div>

                    {/* Virtualized List */}
                    <div className="flex-1 overflow-hidden bg-slate-50 relative">
                         {resources.length === 0 && !loading && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                                <Search size={32} className="mx-auto mb-2 opacity-20" />
                                No resources found matching "{searchTerm}"
                            </div>
                        )}
                        <VirtualList<ResourceEntity>
                            items={resources}
                            height="100%"
                            itemHeight={64}
                            renderItem={(resource, index) => {
                                const isSelected = selectedIds.has(resource.id);
                                return (
                                    <div 
                                        key={resource.id} 
                                        onClick={() => toggleSelection(resource.id)}
                                        className={`
                                            flex items-center text-sm border-b border-slate-100 cursor-pointer transition-colors h-[64px]
                                            ${isSelected ? 'bg-teal-50 hover:bg-teal-100' : 'bg-white hover:bg-slate-50'}
                                        `}
                                    >
                                        <div className="px-4 w-[60px] text-center flex-shrink-0">
                                            <div className={`
                                                w-4 h-4 mx-auto rounded border flex items-center justify-center transition-all
                                                ${isSelected ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-300 bg-white'}
                                            `}>
                                                {isSelected && <Check size={10} strokeWidth={4} />}
                                            </div>
                                        </div>
                                        <div className="px-4 flex-1 min-w-0">
                                            <div className={`font-medium truncate ${isSelected ? 'text-teal-900' : 'text-slate-900'}`}>
                                                {resource.name}
                                            </div>
                                            <div className="flex gap-2 text-[10px] mt-1">
                                                 <span className="font-mono text-slate-400">{resource.id}</span>
                                                 <span className="bg-slate-100 px-1 rounded text-slate-500 border border-slate-200">{resource.resourceKind}</span>
                                            </div>
                                        </div>
                                        <div className="px-4 w-[25%] hidden md:block text-slate-500 truncate">
                                            {resource.description || <span className="text-slate-300 italic">No description</span>}
                                        </div>
                                        <div className="px-4 w-[15%] text-slate-500 truncate">
                                            {resource.resourceType}
                                        </div>
                                        <div className="px-4 w-[20%] flex items-center gap-1.5 text-slate-600 truncate">
                                            <Box size={14} className="text-slate-400 flex-shrink-0" />
                                            <span className="truncate">{resolveSystemName(resource.systemId)}</span>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                    </div>
                </>
            )}

            {/* STEP 2: CONFIGURE */}
            {step === 2 && (
                <div className="h-full flex flex-col bg-slate-50">
                    {/* Bulk Toolbar */}
                    <div className="p-4 bg-white border-b border-slate-200 shadow-sm z-10 flex-shrink-0">
                        <div className="flex items-center gap-4">
                            <div className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                                Apply to all {selectedIds.size} items:
                            </div>
                            <div className="flex-1 max-w-md">
                                <div className="relative">
                                    <Settings className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                    <select 
                                        className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:border-teal-500 bg-slate-50"
                                        value={bulkPlan}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => applyBulkPlan(e.target.value as string)}
                                    >
                                        <option value="">Select Plan...</option>
                                        {Array.from(plansBySystem.keys()).map(sysId => {
                                            const sysName = resolveSystemName(sysId);
                                            const groupPlans = plansBySystem.get(sysId) || [];
                                            return (
                                                <optgroup key={sysId} label={sysName}>
                                                    {groupPlans.map(p => (
                                                        <option key={p.id} value={p.id}>{p.name}</option>
                                                    ))}
                                                </optgroup>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* List */}
                    <div className="flex-1 overflow-y-auto p-4 relative">
                         <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col">
                             {/* Header */}
                             <div className="flex text-left text-sm bg-slate-50 border-b border-slate-200 flex-shrink-0">
                                <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[40%]">Resource</div>
                                <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[25%]">System</div>
                                <div className="px-4 py-3 font-semibold text-slate-600 text-xs uppercase tracking-wider w-[30%]">Plan</div>
                                <div className="px-4 py-3 w-10"></div>
                             </div>
                             
                             {/* Virtual Body */}
                             <div className="flex-1 overflow-hidden relative">
                                <VirtualList<string>
                                    items={Array.from(selectedIds)}
                                    height="100%"
                                    itemHeight={64}
                                    renderItem={(id, index) => {
                                        const resource = resources.find(r => r.id === id); 
                                        const config = configs[id] || { planId: '' };
                                        const isFixed = resource?.resourceKind === ResourceKind.Fixed;
                                        const systemPlans = plans.filter(p => p.systemId === resource?.systemId);

                                        return (
                                            <div key={id} className="flex items-center text-sm border-b border-slate-100 h-[64px] bg-white group hover:bg-slate-50">
                                                <div className="px-4 w-[40%]">
                                                    <div className="font-medium text-slate-900 truncate">{resource?.name || id}</div>
                                                    <div className="flex gap-2 text-[10px] mt-0.5">
                                                        <span className="text-slate-400 font-mono">{id}</span>
                                                        <span className="bg-slate-100 px-1 rounded text-slate-500 border border-slate-200">{resource?.resourceKind}</span>
                                                    </div>
                                                </div>
                                                <div className="px-4 w-[25%]">
                                                    <div className="flex items-center gap-1.5 text-slate-500 text-xs truncate">
                                                        <Box size={12} className="text-slate-400 flex-shrink-0" />
                                                        <span className="truncate">{resolveSystemName(resource?.systemId)}</span>
                                                    </div>
                                                </div>
                                                <div className="px-4 w-[30%]">
                                                    {isFixed ? (
                                                        <div className="flex items-center gap-2 text-slate-400 text-xs italic bg-slate-100 px-2 py-1.5 rounded border border-slate-200 w-fit">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                                            Included in Resource
                                                        </div>
                                                    ) : (
                                                        <select 
                                                            className={`w-full p-2 border rounded-lg text-xs focus:outline-none focus:border-teal-500 transition-colors ${!config.planId ? 'border-red-200 bg-red-50 text-red-800' : 'border-slate-200'}`}
                                                            value={config.planId}
                                                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateConfig(id, 'planId', e.target.value as string)}
                                                            disabled={systemPlans.length === 0}
                                                        >
                                                            <option value="">
                                                                {systemPlans.length === 0 ? 'No plans' : 'Select Plan...'}
                                                            </option>
                                                            {systemPlans.map(p => (
                                                                <option key={p.id} value={p.id}>
                                                                    {p.name} (P{p.priority})
                                                                </option>
                                                            ))}
                                                        </select>
                                                    )}
                                                </div>
                                                <div className="px-4 flex-1 text-right">
                                                    <button 
                                                        onClick={() => handleRemoveInStep2(id)}
                                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Remove"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    }}
                                />
                             </div>
                         </div>
                    </div>
                </div>
            )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white flex justify-between items-center flex-shrink-0">
            <div className="text-sm text-slate-500">
                {selectedIds.size > 0 && (
                    <span className="font-medium text-slate-900">{selectedIds.size} resources selected</span>
                )}
            </div>
            <div className="flex gap-3">
                {step === 2 && (
                    <button 
                        onClick={() => setStep(1)}
                        className="px-4 py-2 text-slate-600 font-medium text-sm hover:bg-slate-100 rounded-lg flex items-center gap-2"
                    >
                        <ChevronLeft size={16} /> Back
                    </button>
                )}
                
                {step === 1 ? (
                    <button 
                        onClick={() => setStep(2)}
                        disabled={selectedIds.size === 0}
                        className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400 text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm"
                    >
                        Next: Configuration <ArrowRight size={16} />
                    </button>
                ) : (
                    <button 
                        onClick={handleConfirm}
                        disabled={!isValid()}
                        className="bg-teal-600 hover:bg-teal-700 disabled:bg-slate-200 disabled:text-slate-400 text-white px-6 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors shadow-sm shadow-teal-600/20"
                    >
                        <CheckCircle2 size={18} /> Confirm & Add Resources
                    </button>
                )}
            </div>
        </div>

      </div>
    </div>
  );
};

export default ResourceSelectionModal;